import type { Organization } from 'services/api';

export type OrgLeaderRow = Organization['orgLeaders'][number];
export type OrgAdminRow = Organization['admins'][number];
export type OrgAdminRequestRow = NonNullable<
  Organization['pendingAdminRequests']
>[number];

/**
 * Unified row for the leaders tab, representing either an org leader, an org admin,
 * or a pending admin request (user asking to become an admin of the org).
 */
export type OrgMemberRow = {
  kind: 'leader' | 'admin' | 'adminRequest';
  /** Leader record id for leaders, user id for admins, request id for admin requests. */
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  schoolEmail: string;
  orgRole: string;
  phoneNumber?: string;
  createdAt?: string;
  lastSeen?: string;
  /** Only relevant for leaders; other kinds have a fixed derived status. */
  inviteStatus?: string;
  inviteSource?: 'admin' | 'user';
  isActive?: boolean;
};

export type LeaderUiStatus =
  | 'active'
  | 'pendingUserAcceptance'
  | 'pendingAdminApproval'
  /** Reserved for a future approval stage; not emitted by current API. */
  | 'pendingStudentAffairs'
  | 'declined';

export function deriveLeaderUiStatus (row: OrgMemberRow): LeaderUiStatus {
  if (row.kind === 'admin') return 'active';
  if (row.kind === 'adminRequest') return 'pendingStudentAffairs';
  const s = (row.inviteStatus || '').toLowerCase();
  const source = row.inviteSource ?? 'admin';
  if (s === 'accepted') return 'active';
  if (s === 'declined') return 'declined';
  if (s === 'requested') {
    if (source === 'admin') return 'pendingUserAcceptance';
    return 'pendingAdminApproval';
  }
  return 'pendingAdminApproval';
}

export function leaderStatusLabel (status: LeaderUiStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'pendingUserAcceptance':
      return 'Pending User Acceptance';
    case 'pendingAdminApproval':
      return 'Pending Admin Approval';
    case 'pendingStudentAffairs':
      return 'Pending Student Affairs';
    case 'declined':
      return 'Declined';
  }
}

export type LeaderRowActions = {
  showEdit: boolean;
  showRemove: boolean;
  showCancel: boolean;
  showReInvite: boolean;
};

export function leaderRowActions (
  ui: LeaderUiStatus,
  kind: OrgMemberRow['kind'] = 'leader',
  opts: { canRemoveAdmin?: boolean } = {},
): LeaderRowActions {
  // Pending admin requests are informational; no row actions.
  if (kind === 'adminRequest') {
    return {
      showEdit: false,
      showRemove: false,
      showCancel: false,
      showReInvite: false,
    };
  }
  // Admins only support removal, and only when the current user has permission.
  if (kind === 'admin') {
    return {
      showEdit: false,
      showRemove: opts.canRemoveAdmin === true,
      showCancel: false,
      showReInvite: false,
    };
  }
  switch (ui) {
    case 'active':
      return {
        showEdit: true,
        showRemove: true,
        showCancel: false,
        showReInvite: false,
      };
    case 'pendingUserAcceptance':
      return {
        showEdit: false,
        showRemove: false,
        showCancel: true,
        showReInvite: false,
      };
    case 'pendingAdminApproval':
      return {
        showEdit: true,
        showRemove: true,
        showCancel: false,
        showReInvite: false,
      };
    case 'pendingStudentAffairs':
      return {
        showEdit: false,
        showRemove: false,
        showCancel: false,
        showReInvite: false,
      };
    case 'declined':
      return {
        showEdit: false,
        showRemove: true,
        showCancel: false,
        showReInvite: true,
      };
  }
}

function formatAdminRoleLabel (role: OrgAdminRow['role']): string {
  // API returns string[] at runtime even though the web type is typed as string.
  // Normalize to a single presentable label.
  const raw: string = Array.isArray(role) ? (role as string[])[0] ?? '' : role;
  if (!raw) return 'Admin';
  return raw
    .split(/[-_\s]+/)
    .filter((w: string) => Boolean(w))
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function leaderToMemberRow (leader: OrgLeaderRow): OrgMemberRow {
  return {
    kind: 'leader',
    id: leader.id,
    userId: leader.userId,
    firstName: leader.firstName,
    lastName: leader.lastName,
    schoolEmail: leader.schoolEmail,
    orgRole: leader.orgRole || '',
    phoneNumber: leader.phoneNumber,
    createdAt: leader.createdAt,
    lastSeen: leader.lastSeen,
    inviteStatus: leader.inviteStatus,
    inviteSource: leader.inviteSource,
    isActive: leader.isActive,
  };
}

function adminToMemberRow (admin: OrgAdminRow): OrgMemberRow {
  return {
    kind: 'admin',
    id: admin.id,
    userId: admin.id,
    firstName: admin.firstName,
    lastName: admin.lastName,
    schoolEmail: admin.schoolEmail,
    orgRole: formatAdminRoleLabel(admin.role),
    createdAt: admin.createdAt,
    lastSeen: admin.lastSeen,
  };
}

function adminRequestToMemberRow (request: OrgAdminRequestRow): OrgMemberRow {
  return {
    kind: 'adminRequest',
    id: request.id,
    userId: request.userId,
    firstName: request.firstName,
    lastName: request.lastName,
    schoolEmail: request.schoolEmail,
    orgRole: 'Admin',
    createdAt: request.createdAt,
  };
}

/**
 * Merge org admins, leaders, and pending admin requests into a single
 * de-duplicated list sorted by name. Precedence when the same user appears in
 * multiple collections: admin > leader > adminRequest.
 */
export function buildOrgMemberRows (
  leaders: OrgLeaderRow[] | undefined,
  admins: OrgAdminRow[] | undefined,
  adminRequests: OrgAdminRequestRow[] | undefined,
): OrgMemberRow[] {
  const adminRows = (admins ?? []).map(adminToMemberRow);
  const adminUserIds = new Set(adminRows.map(r => r.userId));

  const leaderRows = (leaders ?? [])
    .filter(l => !adminUserIds.has(l.userId))
    .map(leaderToMemberRow);
  const leaderUserIds = new Set(leaderRows.map(r => r.userId));

  const requestRows = (adminRequests ?? [])
    .filter(
      r => !adminUserIds.has(r.userId) && !leaderUserIds.has(r.userId),
    )
    .map(adminRequestToMemberRow);

  return sortMembersByName([...adminRows, ...leaderRows, ...requestRows]);
}

export function sortMembersByName (rows: OrgMemberRow[]): OrgMemberRow[] {
  return [...rows].sort((a, b) => {
    const an = `${a.firstName} ${a.lastName}`.trim().toLowerCase();
    const bn = `${b.firstName} ${b.lastName}`.trim().toLowerCase();
    return an.localeCompare(bn);
  });
}
